import React, { useEffect, useRef, useState } from "react";
import { MessageCircleHeart, Send, Sparkles } from "lucide-react";
import client, { authHeaders } from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Child-safe "Ask HikmahVerses" chat widget.
 * Streams tokens from POST /api/chat/ask (SSE) using fetch + ReadableStream.
 */
export default function AskHikmahVerses({ storySeq }) {
  const [messages, setMessages] = useState([]); // {role, content}
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const listRef = useRef(null);

  // Load previous chat history for this story
  useEffect(() => {
    let mounted = true;
    client.get(`/chat/history?story_seq=${storySeq}&limit=50`).then(({ data }) => {
      if (mounted) setMessages(data.messages || []);
    }).catch(() => {});
    return () => { mounted = false; };
  }, [storySeq]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  const send = async (e) => {
    e?.preventDefault();
    const q = input.trim();
    if (!q || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: q }, { role: "assistant", content: "" }]);
    setStreaming(true);

    try {
      const res = await fetch(`${BACKEND_URL}/api/chat/ask`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(),
        },
        body: JSON.stringify({ question: q, story_seq: storySeq }),
      });
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        // Parse SSE events: split by double-newline
        const events = buffer.split("\n\n");
        buffer = events.pop() || "";
        for (const ev of events) {
          const lines = ev.split("\n");
          const isDone = lines.some((l) => l.trim() === "event: done");
          for (const line of lines) {
            if (line.startsWith("data: ")) {
              const chunk = line.slice(6).replace(/\\n/g, "\n");
              if (isDone) continue;
              setMessages((m) => {
                const copy = [...m];
                const last = copy[copy.length - 1];
                if (last && last.role === "assistant") {
                  copy[copy.length - 1] = { ...last, content: last.content + chunk };
                }
                return copy;
              });
            }
          }
        }
      }
    } catch (err) {
      setMessages((m) => {
        const copy = [...m];
        const last = copy[copy.length - 1];
        if (last && last.role === "assistant" && !last.content) {
          copy[copy.length - 1] = { ...last, content: "Sorry — I couldn't reach the assistant just now. Please try again in a moment." };
        }
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div className="hv-card p-6" data-testid="ask-hikmahverses">
      <div className="flex items-center gap-3">
        <div className="hv-icon-circle-coral w-10 h-10 rounded-full flex items-center justify-center shrink-0">
          <MessageCircleHeart size={20} strokeWidth={2.5} />
        </div>
        <div>
          <span className="hv-badge hv-badge-coral">Ask HikmahVerses</span>
          <div className="hv-heading text-hv-navy text-lg mt-1">Have a question about this story?</div>
          <div className="text-hv-slate text-xs">Powered by Claude · answers are gentle and Islamic-safe.</div>
        </div>
      </div>

      {messages.length > 0 && (
        <div ref={listRef} className="mt-5 max-h-[420px] overflow-y-auto space-y-3 pr-2" data-testid="chat-messages">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div
                data-testid={`chat-msg-${m.role}-${i}`}
                className={`max-w-[85%] px-4 py-3 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === "user"
                    ? "bg-hv-navy text-white rounded-tr-sm"
                    : "bg-hv-cream text-hv-navy border border-[#E5DFC8] rounded-tl-sm"
                }`}
                style={m.role === "user" ? { color: "#FFFFFF" } : undefined}
              >
                {m.content || (m.role === "assistant" && streaming ? <span className="inline-flex items-center gap-1"><Sparkles size={12} className="animate-pulse" /> thinking…</span> : "")}
              </div>
            </div>
          ))}
        </div>
      )}

      <form onSubmit={send} className="mt-5 flex gap-2" data-testid="chat-form">
        <input
          data-testid="chat-input"
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask a gentle question about this story…"
          maxLength={500}
          disabled={streaming}
          className="flex-1 px-4 py-3 rounded-full border-2 border-[#E5DFC8] bg-white focus:border-hv-teal focus:outline-none text-sm disabled:opacity-70"
        />
        <button
          type="submit"
          data-testid="chat-send"
          disabled={streaming || !input.trim()}
          className="hv-btn-primary py-2.5 px-5 text-sm inline-flex items-center gap-2 disabled:opacity-50"
        >
          <Send size={16} /> {streaming ? "…" : "Ask"}
        </button>
      </form>
    </div>
  );
}
